import {
  anyMembershipTickets,
  isMembershipType,
  hasTicketWith,
  isStandalone,
  isChildLearning,
  ticketMatches,
  hasYouthTickets,
  notMembershipType,
  findLargestEvent
} from '../../shared/helpers/logic'
import { includesGeneric, includesAdult } from '../../tickets/helpers/logic'
import { has } from 'lodash-es'

/**
 * filters out the required performance IDs from the cart's
 * items, then evaluates whether the required notes are complete.
 * @param cart {Object} the cart is destructured in the function signature.
 * @param cart.items {Object[]} the items currently in the cart
 * @param cart.notes {Object} map of performance IDs:note text
 * @returns {boolean} true if the button should be disabled
 * i.e. if the cart has items that should have notes but don't
 */
export const isMissingRequiredNotes = ({ items, notes }) => {
  if (!items.some(i => i.noteIsRequired)) return false

  const requiredPerformanceIds = items
    .filter(i => i.noteIsRequired)
    .map(i => i.externalId)

  return !requiredPerformanceIds.every(id => has(notes, id))
}

export const isMissingRequiredPreferredRecognition = items => {
  const itemHasPreferredRecognition = items.filter(i =>
    Object.prototype.hasOwnProperty.call(i, 'showPreferredRecognitionName')
  )

  if (itemHasPreferredRecognition.length > 0) {
    if (
      itemHasPreferredRecognition[0].preferredRecognitionName !== null &&
      !itemHasPreferredRecognition[0].preferredRecognitionName.trim().length
    ) {
      return true
    } else return false
  } else return false
}

const includesChild = tickets =>
  tickets.some(ticket =>
    ['child', 'child2', 'youth', 'youth2', 'infant'].includes(ticket.ageGroup)
  )

const hasMemberTickets = tickets => tickets.some(ticket => ticket.member)

const hasEmployeeTickets = tickets =>
  tickets.some(ticket => ticket.pricingGroup == 'employee')

const hasConstituecyTickets = tickets =>
  tickets.some(ticket => ticket.hasConstituencyPricing)

const itemIsDependent = item => {
  if (isMembershipType(item)) {
    return false
  }
  if (!isStandalone(item)) {
    return true
  }
  if (hasTicketWith('pricingGroup', 'addon', item.tickets)) {
    return true
  }
  return false
}

const ticketTotal = (sum, ticket) => {
  return sum + ticket.quantity * ticket.unitPrice
}

export const findDependenciesFor = items => {
  // if there is just one item, we know there are no dependencies
  return items.length === 0 ? [] : items.filter(isStandalone)
}

const sortByNames = items => {
  let sortedByName = []
  let newItems = items

  let sheddAdmission = newItems.find(
    element => element.name === 'Shedd Admission'
  )

  let fourD = newItems.find(element => element.name === '4D Experience')
  let allOther = newItems.filter(
    element =>
      element.name !== 'Shedd Admission' && element.name !== '4D Experience'
  )
  allOther.sort((a, b) => {
    let textA = a.name.toUpperCase()
    let textB = b.name.toUpperCase()
    return textA < textB ? -1 : textA > textB ? 1 : 0
  })

  if (sheddAdmission !== undefined) {
    sortedByName.push(sheddAdmission)
  }
  if (fourD !== undefined) {
    sortedByName.push(fourD)
  }
  let fullSortedByName = sortedByName.concat(allOther)
  return fullSortedByName
}

const getDependentPricingGroup = (dependentTicket, dependencyTicket) => {
  if (
    dependentTicket.pricingGroup === 'addon' &&
    dependencyTicket.pricingGroup === 'default'
  ) {
    return 'addon'
  } else {
    return dependencyTicket.pricingGroup
  }
}

const checkIfNecessaryAdult = (tickets, ticket) => {
  if (!ticket.ageGroup.includes('adult')) {
    return false
  }
  const adultTickets = tickets.filter(ticket =>
    ticket.ageGroup.includes('adult')
  )
  const hasAdultDefault = tickets.find(
    ticket => ticket.pricingGroup === 'default'
  )
  if (includesChild(tickets)) {
    if (adultTickets.length === 1) {
      return true
    }
    if (!hasAdultDefault || ticket.pricingGroup === 'default') {
      return true
    }
  }
  return false
}

const hasAdultTickets = tickets =>
  tickets.some(ticket => ticket.ageGroup.includes('adult'))

export const nonRemovableAgeGroupsFor = (event, items) => {
  let nonRemovableAgeGroups = []
  let memberPricingTicketsExist = true
  if (has(event, 'pricing')) {
    memberPricingTicketsExist = has(event.pricing, 'member')
  }
  items.forEach(item => {
    //prevent non-membership items from having dependencies by checking that both event & item have member tix available
    let memberPricingTixExist = true
    if (has(item, 'pricing')) {
      memberPricingTixExist = has(item.pricing, 'member')
    }
    if (
      itemIsDependent(item) &&
      isStandalone(event) &&
      ((memberPricingTixExist && memberPricingTicketsExist) || //item(addon): both prices, base event: both prices
        (!memberPricingTixExist && memberPricingTicketsExist) || //item(addon): 1 price, base event: both prices
        (!memberPricingTixExist && !memberPricingTicketsExist)) //item(addon): 1 price, base event 1 price
    ) {
      event.tickets.forEach(ticket => {
        const dependentTicket = item.tickets.find(
          itemTicket =>
            ticket.ageGroup === itemTicket.ageGroup ||
            (ticket.ageGroup.includes('youth') &&
              itemTicket.ageGroup.includes('adult'))
        )
        if (dependentTicket) {
          nonRemovableAgeGroups.push(ticket.ageGroup)
        }
      })
    }
  })
  if (
    includesChild(event.tickets) &&
    !nonRemovableAgeGroups.includes('adult')
  ) {
    if (!hasAdultTickets(event.tickets)) {
      if (hasConstituecyTickets(event.tickets)) {
        nonRemovableAgeGroups.push('constituency')
      } else if (hasEmployeeTickets(event.tickets)) {
        nonRemovableAgeGroups.push('employee')
      }
    } else {
      if (memberPricingTicketsExist) {
        nonRemovableAgeGroups.push('adult')
      }
    }
  }
  return nonRemovableAgeGroups
}

const hasMatchingAddOnTicket = (ticket, addOnTickets) => {
  let baseMemember = ticket.member ? 'member' : 'nonMember'
  let baseResidency =
    ticket.pricingGroup == 'chicago' || ticket.pricingGroup == 'illinois'
      ? 'resident'
      : 'nonResident'

  let addOnGenericTicketCount = 0
  let totalAddOnTicketCount = 0
  addOnTickets.forEach(t => {
    if (t.ageGroup.includes('generic')) {
      addOnGenericTicketCount += t.quantity
    }
    totalAddOnTicketCount += t.quantity
  })

  if (
    addOnGenericTicketCount != 0 &&
    addOnGenericTicketCount == totalAddOnTicketCount
  ) {
    return {
      baseEventQuantity: ticket.quantity,
      addOnQuantity: addOnGenericTicketCount
    }
  }

  let addOnMatches = addOnTickets.filter(t => {
    let addOnMemember = t.member ? 'member' : 'nonMember'
    let addOnResidency =
      t.pricingGroup == 'chicago' || t.pricingGroup == 'illinois'
        ? 'resident'
        : 'nonResident'
    return (
      t.ageGroup == ticket.ageGroup &&
      addOnMemember == baseMemember &&
      addOnResidency == baseResidency
    )
  })

  if (addOnMatches.length > 0) {
    return {
      baseEventQuantity: ticket.quantity,
      addOnQuantity: addOnMatches[0].quantity
    }
  } else {
    return false
  }
}

const isInNonRemovableAgeGroups = (nonRemovableAgeGroups, ageGroup) => {
  return nonRemovableAgeGroups.some(ag => {
    return ageGroup.startsWith(ag)
  })
}

export const cannotRemove = (ticket, item, items, checkBaseMinimum = false) => {
  let nonRemovableAgeGroups = nonRemovableAgeGroupsFor(item, items)
  let ticketMatchInItems = false
  const cartItems = items.filter(cartItem => cartItem.id !== item.id)
  let { ageGroup, member } = ticket

  const isAddon = ({ type }) => type.includes('addon')
  const dependents = items.filter(isAddon)

  const isBaseEvent = !isAddon(item) && dependents.length !== 0

  let addOnTickets

  let isAgeGroupNonRemovable = isInNonRemovableAgeGroups(
    nonRemovableAgeGroups,
    ticket.ageGroup
  )

  // if (add on only has generic ticket pricing do the base ticket count compared to add on ticket count)
  if (checkIfNecessaryAdult(item.tickets, ticket) || isAgeGroupNonRemovable) {
    if (isBaseEvent && checkBaseMinimum) {
      addOnTickets = findLargestEvent(dependents).tickets
      return hasMatchingAddOnTicket(ticket, addOnTickets)
    }
    return true
  }

  if (isAgeGroupNonRemovable && cartItems.length !== 0) {
    ticketMatchInItems = cartItems.some(cartItem =>
      cartItem.tickets.some(cartItemTicket => {
        let pricingGroup = getDependentPricingGroup(cartItemTicket, ticket)
        // Sheddweb-165 check if youth tickets exist on base event but not addon, then default to correct ageGroup
        if (ageGroup.includes('youth') && !hasYouthTickets(cartItem.tickets)) {
          ageGroup = member ? 'child' : 'adult'
        }
        return ticketMatches(cartItemTicket, ageGroup, member, pricingGroup)
      })
    )
    if (
      !ticketMatchInItems &&
      ['illinois', 'chicago'].includes(ticket.pricingGroup) &&
      hasTicketWith('pricingGroup', 'default', item.tickets)
    ) {
      ticketMatchInItems = cartItems.some(cartItem =>
        cartItem.tickets.some(cartItemTicket => {
          // Sheddweb-165 check if youth tickets exist on base event but not addon, then default to correct ageGroup
          if (
            ageGroup.includes('youth') &&
            !hasYouthTickets(cartItem.tickets)
          ) {
            ageGroup = member ? 'child' : 'adult'
          }
          return ticketMatches(cartItemTicket, ageGroup, member)
        })
      )
    }
  }

  // this section looks for generic tickets in the base event that have aged add ons
  const isRequiredGeneric = (baseTickets, ticket) => {
    if (includesGeneric(baseTickets) && !includesAdult(baseTickets)) {
      return ticket.ageGroup.includes('generic')
    } else return false
  }

  // logic for SHEDDWEB-199
  // similar to the logic for preventing ticket reduction,
  // we check the quantity of other base event tickets against
  // the add-on quantity before allowing items to be removed
  if (isBaseEvent) {
    addOnTickets = findLargestEvent(dependents).tickets

    // if the largest add on only has generic priced tickets, count base tickets compared to add on tickets
    const matchingAddOnTicket = hasMatchingAddOnTicket(ticket, addOnTickets)
    let cantRemoveAddOn = matchingAddOnTicket
      ? matchingAddOnTicket.addOnQuantity > 0
      : matchingAddOnTicket
    return cantRemoveAddOn || isRequiredGeneric(item.tickets, ticket)
  }

  return ticketMatchInItems
}

export const getRecommendedMembership = (
  maxAdults,
  maxChildren,
  childLearningInCart
) => {
  let membershipCTAText = null
  if (maxChildren == 0) {
    if (childLearningInCart) {
      membershipCTAText = 'Family'
    } else if (maxAdults == 1) {
      membershipCTAText = 'Supporter'
    } else if (maxAdults == 2) {
      membershipCTAText = 'Partner'
    }
  } else if (maxAdults > 0) {
    if (maxAdults < 3) {
      membershipCTAText = 'Family'
    } else {
      membershipCTAText = 'Advocate'
    }
  }
  return membershipCTAText
}

export const hasMembershipDependentItems = items =>
  items.some(item => {
    if (notMembershipType(item)) {
      return hasMemberTickets(item.tickets)
    } else {
      return false
    }
  })

export const sumCartItems = items => {
  return items.reduce((total, item) => {
    let totalTicketPrice = item.price
    if (item.tickets) {
      totalTicketPrice = item.tickets.reduce(ticketTotal, 0)
    }
    return total + totalTicketPrice
  }, 0)
}

export const allItemsHaveMembershipTickets = (items, childrenAllowed) => {
  return items.every(item => {
    if (!isChildLearning(item.pricing.member) || childrenAllowed) {
      const memberPricingTicketsExist = has(item.pricing, 'member')
      return memberPricingTicketsExist
        ? anyMembershipTickets(item.tickets)
        : true
    } else {
      return true
    }
  })
}

export const membershipInCart = items =>
  items.some(item => isMembershipType(item) && !item.gift)

export const needsToUpdateTickets = (items, membership) => {
  const eventItems = items.filter(notMembershipType)
  return (
    (membershipInCart(items) || membership.active) &&
    !allItemsHaveMembershipTickets(eventItems, membership.childrenAllowed)
  )
}

export const sortByAge = priceGroups => {
  const ageSort = [
    'employee',
    'adult',
    'adult2',
    'youth',
    'youth2',
    'child',
    'child2',
    'infant'
  ].filter(age => Object.prototype.hasOwnProperty.call(priceGroups, age))
  return ageSort.map(key => priceGroups[key])
}

export const eventsGroupedByDateWithDependency = eventsGroupedByDate => {
  return Object.entries(eventsGroupedByDate).map(([date, items], index) => {
    const dependencies = findDependenciesFor(items)
    return {
      date,
      dependencies,
      items
    }
  })
}

export const sortedEventsGroupedByDate = (eventsGroupedByDate, needsUpdate) => {
  return eventsGroupedByDate.map(({ items, ...dateData }) => {
    const itemsSortedByName = sortByNames(items)
    return { items: itemsSortedByName, ...dateData }
  })
}

export {
  isStandalone,
  totalTickets,
  clearTicketsAppSessionData,
  hasPriceTypes,
  anyMembershipTickets,
  hitEmployeeMax,
  hitGeneralAdmissionMax,
  isMembershipType,
  addOnMinimums,
  isChildLearning,
  notMembershipType
} from '../../shared/helpers/logic'
